import { Dayjs, isDayjs } from "dayjs";
import * as yup from "yup";
import { validator } from "@/utils/validator";
import { MAX_CHARACTER_FULLNAME_VALIDATION } from "@/constants/common";

const MAX_TOTAL_LEARNING_HOURS = 10000;
const MAX_WEEKLY_LEARNING_HOURS = 24 * 7;
const MAX_LEARNED_VOCAB_COUNT = 10000;
const MAX_EXAM_COUNT = 1000;
const MAX_EXAM_SCORE = 1000;

const LAST_LOGIN_DATE_LABELS = { startLabel: "最終ログイン（開始）", endLabel: "最終ログイン（終了）" } as const;
const INVALID_DATE_FORMAT = "${label}の日付形式が正しくありません";
const RANGE_TO_MESSAGE = "${label}は下限以上で入力してください";

const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function toGteFrom(fromKey: string) {
  return function (this: yup.TestContext, value: number | null | undefined) {
    const from = (this.parent as Record<string, unknown>)[fromKey];
    if (!isFiniteNumber(value) || !isFiniteNumber(from)) return true;
    return value >= from;
  };
}

export const learningProgressFilterSchema = validator.object({
  // 氏名 — server caps fullName at 104 chars (MAX_CHARACTER_FULLNAME_VALIDATION); mirror that limit here.
  fullName: validator
    .string()
    .label("氏名")
    .emptyToNull()
    .max(MAX_CHARACTER_FULLNAME_VALIDATION)
    .nullable()
    .notRequired(),

  // 所属機関 — multi-select; values are organization IDs as strings.
  rsoCompanyIds: validator
    .array()
    .of(validator.string())
    .label("所属機関")
    .notRequired(),

  // 国籍 — multi-select; values are nationality IDs as strings.
  nationalityIds: validator
    .array()
    .of(validator.string())
    .label("国籍")
    .notRequired(),

  // 試験予定 — multi-select; values are exam IDs as strings.
  examIds: validator
    .array()
    .of(validator.string())
    .label("試験予定")
    .notRequired(),

  // 学習進捗 — multi-select progress buckets (0-19/20-39/40-59/60-79/80-100).
  curriculumProgresses: validator
    .array()
    .of(validator.string())
    .label("学習進捗")
    .notRequired(),

  // 日本語能力 — multi-select Japanese language level codes.
  japaneseLanguageLevels: validator
    .array()
    .of(validator.string())
    .label("日本語能力")
    .notRequired(),

  lastLoginDateFrom: validator
    .mixed<Dayjs>()
    .label(LAST_LOGIN_DATE_LABELS.startLabel)
    .nullable()
    .notRequired()
    .test("isDayjs", INVALID_DATE_FORMAT, (v) => v == null || isDayjs(v))
    .isBeforeDateOfField("lastLoginDateTo", LAST_LOGIN_DATE_LABELS),
  lastLoginDateTo: validator
    .mixed<Dayjs>()
    .label(LAST_LOGIN_DATE_LABELS.endLabel)
    .nullable()
    .notRequired()
    .test("isDayjs", INVALID_DATE_FORMAT, (v) => v == null || isDayjs(v))
    .isAfterDateOfField("lastLoginDateFrom", LAST_LOGIN_DATE_LABELS),

  learningTimeFrom: validator
    .number()
    .label("学習時間（合計）")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .min(0)
    .max(MAX_TOTAL_LEARNING_HOURS)
    .nullable()
    .notRequired(),
  learningTimeTo: validator
    .number()
    .label("学習時間（合計）")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .min(0)
    .max(MAX_TOTAL_LEARNING_HOURS)
    .nullable()
    .notRequired()
    .test("to-gte-from", RANGE_TO_MESSAGE, toGteFrom("learningTimeFrom")),

  thisWeekLearningTimeFrom: validator
    .number()
    .label("学習時間（今週）")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .min(0)
    .max(MAX_WEEKLY_LEARNING_HOURS)
    .nullable()
    .notRequired(),
  thisWeekLearningTimeTo: validator
    .number()
    .label("学習時間（今週）")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .min(0)
    .max(MAX_WEEKLY_LEARNING_HOURS)
    .nullable()
    .notRequired()
    .test("to-gte-from", RANGE_TO_MESSAGE, toGteFrom("thisWeekLearningTimeFrom")),

  learnedVocabCountFrom: validator
    .number()
    .label("覚えた単語数")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(MAX_LEARNED_VOCAB_COUNT)
    .nullable()
    .notRequired(),
  learnedVocabCountTo: validator
    .number()
    .label("覚えた単語数")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(MAX_LEARNED_VOCAB_COUNT)
    .nullable()
    .notRequired()
    .test("to-gte-from", RANGE_TO_MESSAGE, toGteFrom("learnedVocabCountFrom")),

  mockExamCountFrom: validator
    .number()
    .label("模試実施回数")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(MAX_EXAM_COUNT)
    .nullable()
    .notRequired(),
  mockExamCountTo: validator
    .number()
    .label("模試実施回数")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(MAX_EXAM_COUNT)
    .nullable()
    .notRequired()
    .test("to-gte-from", RANGE_TO_MESSAGE, toGteFrom("mockExamCountFrom")),

  latestMockExamScoreFrom: validator
    .number()
    .label("直近模試結果")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(MAX_EXAM_SCORE)
    .nullable()
    .notRequired(),
  latestMockExamScoreTo: validator
    .number()
    .label("直近模試結果")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(MAX_EXAM_SCORE)
    .nullable()
    .notRequired()
    .test("to-gte-from", RANGE_TO_MESSAGE, toGteFrom("latestMockExamScoreFrom")),

  lastExamScoreFrom: validator
    .number()
    .label("前回本試験点数")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(MAX_EXAM_SCORE)
    .nullable()
    .notRequired(),
  lastExamScoreTo: validator
    .number()
    .label("前回本試験点数")
    .emptyToNull()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(MAX_EXAM_SCORE)
    .nullable()
    .notRequired()
    .test("to-gte-from", RANGE_TO_MESSAGE, toGteFrom("lastExamScoreFrom")),
});
